import React from 'react';
import { styled } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import Button from '@atlaskit/button';
import CloseIcon from '@atlaskit/icon/glyph/editor/close';
import Lozenge from '@atlaskit/lozenge';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import commonMessages from '../../../../../common/messages';
import type { Priority } from '../../../../../common/ui/types';
import messages from './messages';

type Props = {
	onRemove: (priority: Priority) => void;
	onSetDefault: (priority: Priority) => void;
	priorities: Priority[];
};

const Row = ({
	priority,
	onRemove,
	onSetDefault,
}: {
	priority: Priority;
	onRemove?: Props['onRemove'];
	onSetDefault: Props['onSetDefault'];
}) => {
	const { formatMessage } = useIntl();

	const defaultControl = priority.isDefault ? (
		<Box xcss={lozengeWrapperStyles}>
			<Lozenge>{formatMessage(commonMessages.defaultPriorityLozenge)}</Lozenge>
		</Box>
	) : (
		<SetDefaultWrapper data-control="set-default">
			<Button appearance="subtle-link" spacing="compact" onClick={() => onSetDefault(priority)}>
				{formatMessage(messages.setAsDefaultButton)}
			</Button>
		</SetDefaultWrapper>
	);

	return (
		<RowWrapper>
			<Avatar appearance="circle" label={priority.name} size="xsmall" src={priority.iconUrl} />
			<span>{priority.name}</span>
			<ActionsWrapper>
				{defaultControl}
				{onRemove && (
					<Button
						appearance="subtle-link"
						spacing="compact"
						title={formatMessage(messages.removePriorityButton)}
						iconBefore={<CloseIcon label="" />}
						onClick={() => onRemove(priority)}
					/>
				)}
			</ActionsWrapper>
		</RowWrapper>
	);
};

export default function PrioritiesList({ priorities, onRemove, onSetDefault }: Props) {
	const onRemovePriority = priorities.length > 1 ? onRemove : undefined;

	return (
		<div>
			{priorities.map((priority) => (
				<Row
					key={priority.id}
					priority={priority}
					onSetDefault={onSetDefault}
					onRemove={onRemovePriority}
				/>
			))}
		</div>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RowWrapper = styled.div({
	display: 'flex',
	justifyContent: 'start',
	alignItems: 'center',
	flexDirection: 'row',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `1px solid ${token('color.border', colors.N40)}`,
	padding: `${token('space.100', '8px')} ${token('space.150', '12px')}`,
	marginBottom: token('space.100', '8px'),
	borderRadius: '4px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:hover [data-control="set-default"] button': {
		opacity: 1,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:last-child': {
		marginBottom: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > span': {
		marginLeft: token('space.100', '8px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsWrapper = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'center',
	flexDirection: 'row',
	flexGrow: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SetDefaultWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > button': {
		opacity: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > button:focus': {
		opacity: 1,
	},
});

const lozengeWrapperStyles = xcss({
	marginRight: 'space.150',
});
